import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './styles.module.scss'
import {
    clientColumns,
    claimColumns,
    introducerOptions,
    startingBalanceOptions,
    quantumValueOptions,
} from './data'
import api from '../../../helpers/apiRequester'
import { useApprovedLenders } from '../../../helpers/approvedLenders'
import { getLoginUrl } from '../../../helpers/authConfig'
import { Loading, Report } from 'notiflix'
import Modal from '../../reusable/modal'
import { useAuthUserData } from '../../../authUserProvider'

const BASE_URL =
    process.env.REACT_APP_API_URL === 'http://localhost:8080/api'
        ? 'http://localhost:8080/report'
        : 'https://case-delivery.sentinellegal.co.uk/report'

type ReportFileType = 'csv' | 'xlsx'

interface ReportLink {
    id: string
    title: string
    url: string
    description?: string
    /** Optional long-form explanation shown in a "Read more" modal */
    fullDescription?: React.ReactNode
    category: string
    /** Download file type; defaults to csv */
    fileType?: ReportFileType
}

const FULL_CLIENT_EXPORT_SOURCES = [
    'PCP Sentinel',
    'PCPMO26',
    'MFC',
    'MB Finance Check',
    'Check Finance (Mail Referral)',
    'SalesOne',
    'Sentinel Matters',
    'PCP Referral',
    'AF Leads',
    'FairClaim',
    'FFJ',
    'Sentinel Reporting Data Source (SRDS)',
    'Funding Additional',
    'Funding Extended',
    'SID',
    'LC Leads (ADL)',
    'Gorilla',
    'Spyro MFN',
    'Allegiant',
    'Sanderson Drake',
    'Vision Launch',
    'ClaimCo',
    'OA Genie Sync',
    'Credit Report Client Data (ViewThru)',
    'ZappedCases (clients and manual_clients)',
]

const FULL_CLIENT_EXPORT_EXCLUDED = [
    'Irresponsible Lending',
    'Military Hearing Loss',
    'Malpractice',
    'Boshh Import',
    'Direct Import',
    'Consolidated Clients',
]

const FullClientExportDescription = () => (
    <>
        <p>
            Pulls every row from the <code>clients</code> table of each introducer database below and combines them
            into a single Excel workbook. Column names differ between databases (for example{' '}
            <code>firstname</code> vs <code>first_name</code>, <code>telephone</code> vs <code>phone</code>,{' '}
            <code>email</code> vs <code>email_address</code>) and are normalised to one set of headers.
        </p>
        <h5>Sheets</h5>
        <ul>
            <li>
                <strong>Clients</strong> - one row per client record from every source
            </li>
            <li>
                <strong>Summary</strong> - number of clients pulled from each originating schema, how many
                duplicates, test emails and blank or single-letter names were removed, a status for each source and
                overall totals
            </li>
        </ul>
        <h5>Data sources</h5>
        <ul>
            {FULL_CLIENT_EXPORT_SOURCES.map((source) => (
                <li key={source}>{source}</li>
            ))}
        </ul>
        <h5>Columns (Clients sheet)</h5>
        <ul>
            <li>
                <code>client_id</code> - the client&apos;s id within its source database
            </li>
            <li>
                <code>firstname</code>, <code>lastname</code>, <code>dob</code> (YYYY-MM-DD), <code>email</code>,{' '}
                <code>phone</code>, <code>postcode</code>
            </li>
            <li>
                <code>credit_report_link</code> - where the source holds one. For Credit Report Client Data this is
                the stored report key rather than a URL.
            </li>
            <li>
                <code>originating_schema</code> - the source database and table, e.g.{' '}
                <code>pcpSentinel.clients</code>
            </li>
        </ul>
        <h5>Notes</h5>
        <ul>
            <li>
                Records are de-duplicated across databases on first name, last name and date of birth (ignoring
                case, spaces and punctuation). The first occurrence is kept, in the order the sources are listed
                above, so <code>originating_schema</code> shows where that person was first found. Records missing
                any of those three fields are never merged. The number of duplicates removed per source is shown on
                the Summary sheet.
            </li>
            <li>Excluded databases: {FULL_CLIENT_EXPORT_EXCLUDED.join(', ')}.</li>
            <li>
                Test and internal accounts are removed: any email containing &quot;test&quot; as a word (e.g.{' '}
                <code>test@</code>, <code>test1@</code>, <code>tester@</code>), any Sentinel email address, known
                developer test domains, and <code>jay.exton@live.co.uk</code>. The number removed per source is shown
                on the Summary sheet.
            </li>
            <li>
                Clients with a blank first name or last name, or where either is only a single letter (e.g.
                &quot;J&quot; or &quot;A.&quot;), are excluded as incomplete records; the count per source is shown on
                the Summary sheet.
            </li>
            <li>The workbook is large (several hundred thousand rows) and can take a little while to download.</li>
        </ul>
    </>
)

interface CustomReportConfig {
    tableGroup: 'clients' | 'claims'
    selectedColumns: string[]
    dateFrom: string
    dateTo: string
    cancelled: boolean | 'all'
    hasPolicyNumber: boolean | 'all'
    introducer: string | 'all'
    lenders: string | 'all'
    hasStartingBalance: boolean | 'all'
    hasQuantumValue: boolean | 'all'
}

const reportLinks: ReportLink[] = [
    // Introducer Reports
    {
        id: 'all-introducer-claims',
        title: 'All Introducer Claims',
        url: `${BASE_URL}/aic-report`,
        description: 'Complete overview of all introducer claims data',
        category: 'Introducer Reports',
    },
    {
        id: 'all-introducer-claims-policy',
        title: 'Policy Number Introducer Claims ',
        url: `${BASE_URL}/aicp-report`,
        description: 'Introducer claims including policy number information',
        category: 'Introducer Reports',
    },
    {
        id: 'active-duplicate-policy-reports',
        title: 'Duplicate Policy Number Report',
        url: `${BASE_URL}/dupe-policy-numbers-report`,
        description: 'Reports on active claims with duplicate policy numbers for analysis',
        category: 'Introducer Reports',
    },
    {
        id: 'bad-lender-active-claims-report',
        title: 'Bad Lender Active Claims Report',
        url: `${BASE_URL}/bad-lender-active-claims-report`,
        description: 'Reports on active claims with bad lenders for analysis',
        category: 'Introducer Reports',
    },
    {
        id: 'acummulative-claims-quantum-value-report',
        title: 'Accumulative Quantum Values Report',
        url: `${BASE_URL}/accumatlive-quantum-value-report`,
        description: 'A list off client accumlative quantum values',
        category: 'Introducer Reports',
    },
    {
        id: 'all-claims-quantum-value-report',
        title: 'All Claims Quantum Values Report',
        url: `${BASE_URL}/all-quantum-value-report`,
        description: 'A list off all claims quantum values',
        category: 'Introducer Reports',
    },
    {
        id: 'cancelled-clients-report',
        title: 'Cancelled Clients Report',
        url: `${BASE_URL}/cancelled-clients-report`,
        description:
            'Excel workbook of cancelled claims and clients whose claims are all cancelled across Zapped Cases and introducer databases.',
        category: 'Introducer Reports',
        fileType: 'xlsx',
    },

    // General Reports
    {
        id: 'oa-transfer-stats',
        title: 'OA Transfer Data',
        url: `${BASE_URL}/oa-transfer-stats`,
        description: 'Office automation transfer statistics',
        category: 'General Reports',
    },
    {
        id: 'website-claims-report',
        title: 'Organic Website Leads',
        url: `${BASE_URL}/website-claims-report`,
        description: 'Website-generated lead analytics',
        category: 'General Reports',
    },
    {
        id: 'fos-cfa-transfer-report',
        title: 'FOS CFA Transfers',
        url: `${BASE_URL}/fos-cfa-transfer-report`,
        description: 'Financial Ombudsman Service CFA transfer data',
        category: 'General Reports',
    },
    {
        id: 'malpractice-report',
        title: 'Malpractice Report',
        url: `${BASE_URL}/malpractice-report`,
        description: 'Malpractice report',
        category: 'General Reports',
    },
    {
        id: 'daily-policy-number-report',
        title: 'Daily Policy Number Report',
        url: `${BASE_URL}/daily-policy-number-report`,
        description: 'CSV of policy numbers assigned on a selected date',
        category: 'General Reports',
    },
    {
        id: 'pcpmo26-ob-report',
        title: 'PCPMO26 OB Report',
        url: `${BASE_URL}/pcpmo26-ob-report`,
        description: 'PCPMO26 Open Banking Report',
        category: 'General Reports',
    },
    {
        id: 'nihl-mhl-campaign-report',
        title: 'NIHL/MHL Campaign Report',
        url: `${BASE_URL}/nihl-mhl-campaign-report`,
        description:
            'Military Hearing Loss Campaign Report',
        category: 'General Reports',
    },
    {
        id: 'full-client-export-report',
        title: 'Full Client Export',
        url: `${BASE_URL}/full-client-export-report`,
        description:
            'Excel workbook of every client record across all Sentinel introducer databases, with a summary sheet of client counts per database.',
        fullDescription: <FullClientExportDescription />,
        category: 'General Reports',
        fileType: 'xlsx',
    },
    // Woodville Reports
    {
        id: 'funder-report',
        title: 'Funder Report',
        url: `${BASE_URL}/funder-report-v4`,
        description: 'Comprehensive funding analysis and statistics',
        category: 'Woodville',
    },
    {
        id: 'full-data-report',
        title: 'Woodville Data Report',
        url: `${BASE_URL}/full-db-case-report`,
        description: 'Complete database case information',
        category: 'Woodville',
    },
    {
        id: 'pad-apps-report',
        title: 'Pad Apps',
        url: `${BASE_URL}/pad-apps-report`,
        description: 'Pad applications and usage statistics',
        category: 'Woodville',
    },

    // LOA Reports
    {
        id: 'loa-dsar-dob-conf-report',
        title: 'LOAs to Confirm DOB (WEBSNT)',
        url: `${BASE_URL}/loa-dsar-dob-conf-report`,
        description: 'Letters of Authority requiring date of birth confirmation',
        category: 'LOA Reports',
    },
    {
        id: 'loa-dsar-report',
        title: 'LOAs Sent from Dashboard',
        url: `${BASE_URL}/loa-dsar-report`,
        description: 'Letters of Authority sent through the dashboard system',
        category: 'LOA Reports',
    },
    {
        id: 'foa-v2-report',
        title: 'LOA Requests',
        url: `${BASE_URL}/foa-v2-report`,
        description: 'Current Form of Authority requests',
        category: 'LOA Reports',
    },
    {
        id: 'foa-v2-report-websnt',
        title: 'WEBSNT LOA Requests (Outdated)',
        url: `${BASE_URL}/foa-v2-report__websnt`,
        description: 'Legacy WEBSNT Form of Authority requests',
        category: 'LOA Reports',
    },
    {
        id: 'loa-options-report',
        title: 'LOA Options Report',
        url: `${BASE_URL}/loa-options-report`,
        description: 'LOA options report',
        category: 'LOA Reports',
    },
]

export const ReportLinksView = () => {
    const location = useLocation()
    const authUser = useAuthUserData()
    const customReportRef = useRef<HTMLDivElement | null>(null)
    // Approved lenders come from the centralised list (Admin > Approved Lenders).
    const { options: lenderOptions, loading: lendersLoading } = useApprovedLenders()
    const [selectedCategory, setSelectedCategory] = useState<string>('all')
    const [dailyPolicyDate, setDailyPolicyDate] = useState<string>('')
    const [customReportConfig, setCustomReportConfig] = useState<CustomReportConfig>({
        tableGroup: 'clients',
        selectedColumns: ['firstname', 'lastname', 'email'],
        dateFrom: '',
        dateTo: '',
        cancelled: 'all',
        hasPolicyNumber: 'all',
        introducer: 'all',
        lenders: 'all',
        hasStartingBalance: 'all',
        hasQuantumValue: 'all',
    })
    const [showDateRange, setShowDateRange] = useState(false)
    const [showFilters, setShowFilters] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [detailsLink, setDetailsLink] = useState<ReportLink | null>(null)

    const renderDescription = (link: ReportLink) => {
        if (!link.description) return null
        return (
            <p className={styles.reportDescription}>
                {link.description}
                {link.fullDescription && (
                    <>
                        {' '}
                        <button
                            type="button"
                            className={styles.readMoreLink}
                            onClick={() => setDetailsLink(link)}
                            aria-label={`Read more about ${link.title}`}
                        >
                            Read more
                        </button>
                    </>
                )}
            </p>
        )
    }

    const canDownloadFullClientExport =
        String(authUser?.email || '')
            .trim()
            .toLowerCase() === 'james@sentinellegal.co.uk'
    const visibleReportLinks = reportLinks.filter(
        (link) => link.id !== 'full-client-export-report' || canDownloadFullClientExport
    )
    const categories = ['all', ...Array.from(new Set(visibleReportLinks.map((link) => link.category)))]

    const filteredLinks =
        selectedCategory === 'all'
            ? visibleReportLinks
            : visibleReportLinks.filter((link) => link.category === selectedCategory)

    const groupedLinks = visibleReportLinks.reduce((acc, link) => {
        if (!acc[link.category]) {
            acc[link.category] = []
        }
        acc[link.category].push(link)
        return acc
    }, {} as Record<string, ReportLink[]>)

    const handleColumnToggle = (columnId: string) => {
        setCustomReportConfig((prev) => ({
            ...prev,
            selectedColumns: prev.selectedColumns.includes(columnId)
                ? prev.selectedColumns.filter((id) => id !== columnId)
                : [...prev.selectedColumns, columnId],
        }))
    }

    const handleSelectAllColumns = () => {
        const currentColumns = customReportConfig.tableGroup === 'clients' ? clientColumns : claimColumns
        setCustomReportConfig((prev) => ({
            ...prev,
            selectedColumns: currentColumns.map((col) => col.id),
        }))
    }

    const handleClearAllColumns = () => {
        setCustomReportConfig((prev) => ({
            ...prev,
            selectedColumns: [],
        }))
    }

    useEffect(() => {
        Loading.circle('Authenticating...')
        api.post(`${process.env.REACT_APP_API_URL}/reports-auth`)
            .then((res) => {
                Loading.remove()
            })
            .catch((error: any) => {
                Loading.remove()
                if (error?.response?.status === 401) {
                    Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                        window.location.href = getLoginUrl()
                    })
                } else {
                    // console.log('Whoops!', error) //DEBUG
                    // window.location.href = getLoginUrl()
                }
            })
    }, [])

    // Initialise Daily Policy Number Report date to yesterday
    useEffect(() => {
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
        const iso = yesterday.toISOString().split('T')[0]
        setDailyPolicyDate(iso)
    }, [])

    // Scroll the Custom Report builder into view when navigated to via /report-links#custom-report
    useEffect(() => {
        if (location.hash === '#custom-report' && customReportRef.current) {
            // Defer to next frame so the layout is settled before scrolling
            requestAnimationFrame(() => {
                customReportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
        }
    }, [location.hash])

    const generateCustomReport = async () => {
        setIsLoading(true)
        setLoadingMessage('Generating custom report...')

        try {
            const params = new URLSearchParams({
                tableGroup: customReportConfig.tableGroup,
                columns: customReportConfig.selectedColumns.join(','),
                dateFrom: customReportConfig.dateFrom,
                dateTo: customReportConfig.dateTo,
            })

            console.log('customReportConfig', customReportConfig)

            // Add filter parameters for claims
            if (customReportConfig.tableGroup === 'claims') {
                params.append('cancelled', customReportConfig.cancelled.toString())
                params.append('has_policy_number', customReportConfig.hasPolicyNumber.toString())
                params.append('introducer', customReportConfig.introducer)
                params.append('lenders', customReportConfig.lenders)
                params.append('has_starting_balance', customReportConfig.hasStartingBalance.toString())
                params.append('has_quantum_value', customReportConfig.hasQuantumValue.toString())
            }

            const url = `${BASE_URL}/custom-report?${params.toString()}&apiKey=${process.env.REACT_APP_REPORT_API_KEY}`
            console.log('url', url)
            // http://localhost:8080/report/custom-report?tableGroup=claims&columns=policy_number%2Ccase_ref&dateFrom=&dateTo=&cancelled=all&has_policy_number=all&introducer=all            // // Create a temporary link element to trigger download
            const link = document.createElement('a')
            link.href = url
            link.download = `custom-${customReportConfig.tableGroup}-report.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Simulate some processing time
            await new Promise((resolve) => setTimeout(resolve, 2000))
        } catch (error) {
            console.error('Error generating report:', error)
        } finally {
            setIsLoading(false)
            setLoadingMessage('')
        }
    }

    const handleReportDownload = async (url: string, reportName: string, fileType: ReportFileType = 'csv') => {
        setIsLoading(true)
        setLoadingMessage(`Generating ${reportName}...`)

        try {
            // Create a temporary link element to trigger download

            // Add API key header to URL
            const apiKey = process.env.REACT_APP_REPORT_API_KEY
            let urlSpacer = !url.includes('?') ? '?' : '&'
            url = `${url}${urlSpacer}apiKey=${apiKey}`

            if (reportName === 'Full Client Export') {
                const response = await api.get(url, { responseType: 'blob' })
                const blobUrl = window.URL.createObjectURL(response.data)
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = `${reportName.toLowerCase().replace(/\s+/g, '-')}.${fileType}`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
                return
            }

            const link = document.createElement('a')
            link.href = url
            link.download = `${reportName.toLowerCase().replace(/\s+/g, '-')}.${fileType}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Simulate some processing time
            await new Promise((resolve) => setTimeout(resolve, 1500))
        } catch (error) {
            console.error('Error downloading report:', error)
        } finally {
            setIsLoading(false)
            setLoadingMessage('')
        }
    }

    const handleDailyPolicyReportDownload = async () => {
        if (!dailyPolicyDate) return
        setIsLoading(true)
        setLoadingMessage('Generating Daily Policy Number Report...')
        try {
            const apiKey = process.env.REACT_APP_REPORT_API_KEY
            const url = `${BASE_URL}/daily-policy-number-report?date=${dailyPolicyDate}&apiKey=${apiKey}`
            const link = document.createElement('a')
            link.href = url
            link.download = `daily-policy-number-report-${dailyPolicyDate}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            await new Promise((resolve) => setTimeout(resolve, 1200))
        } catch (error) {
            console.error('Error downloading Daily Policy Number Report:', error)
        } finally {
            setIsLoading(false)
            setLoadingMessage('')
        }
    }

    const currentColumns = customReportConfig.tableGroup === 'clients' ? clientColumns : claimColumns

    return (
        <div className={styles.reportLinksView}>
            {/* Loading Overlay */}
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingSpinner}></div>
                        <p className={styles.loadingText}>{loadingMessage}</p>
                    </div>
                </div>
            )}

            <div className={styles.container}>
                {/* Header with Custom Report */}
                <div className={styles.header}>
                    {/* Custom Report Section - Always Visible */}
                    <div id="custom-report" ref={customReportRef} className={styles.customReportSection}>
                        <div className={styles.customReportHeader}>
                            <h2 className={styles.customReportTitle}>Create Custom Report</h2>
                        </div>

                        <div className={styles.customReportBuilder}>
                            <div className={styles.configRow}>
                                <div className={styles.configGroup}>
                                    <label className={styles.configLabel}>Table Group</label>
                                    <div className={styles.tableGroupButtons}>
                                        <button
                                            type="button"
                                            className={`${styles.tableGroupButton} ${
                                                customReportConfig.tableGroup === 'clients' ? styles.active : ''
                                            }`}
                                            onClick={() =>
                                                setCustomReportConfig((prev) => ({
                                                    ...prev,
                                                    tableGroup: 'clients',
                                                    selectedColumns: [],
                                                    hasStartingBalance: 'all',
                                                    hasQuantumValue: 'all',
                                                }))
                                            }
                                        >
                                            Clients
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.tableGroupButton} ${
                                                customReportConfig.tableGroup === 'claims' ? styles.active : ''
                                            }`}
                                            onClick={() =>
                                                setCustomReportConfig((prev) => ({
                                                    ...prev,
                                                    tableGroup: 'claims',
                                                    selectedColumns: [],
                                                    hasStartingBalance: 'all',
                                                    hasQuantumValue: 'all',
                                                }))
                                            }
                                        >
                                            Claims
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.columnsSection}>
                                <div className={styles.columnsHeader}>
                                    <label className={styles.configLabel}>
                                        Select Columns ({customReportConfig.selectedColumns.length} selected)
                                    </label>
                                    <div className={styles.columnActions}>
                                        <button
                                            type="button"
                                            onClick={handleSelectAllColumns}
                                            className={styles.actionButton}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleClearAllColumns}
                                            className={styles.actionButton}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.columnsGrid}>
                                    {currentColumns.map((column) => (
                                        <label key={column.id} className={styles.columnCheckbox}>
                                            <input
                                                type="checkbox"
                                                checked={customReportConfig.selectedColumns.includes(column.id)}
                                                onChange={() => handleColumnToggle(column.id)}
                                            />
                                            <span>{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className={styles.dateRangeRow}>
                                <div className={styles.configGroup}>
                                    <div className={styles.dateRangeHeader}>
                                        <label className={styles.configLabel}>Date Range (CFA Signed Date)</label>
                                        <button
                                            type="button"
                                            className={`${styles.dateToggleButton} ${styles.dateToggleButtonRight} ${
                                                showDateRange ? styles.active : ''
                                            }`}
                                            onClick={() => {
                                                setShowDateRange(!showDateRange)
                                                if (showDateRange) {
                                                    setCustomReportConfig((prev) => ({
                                                        ...prev,
                                                        dateFrom: '',
                                                        dateTo: '',
                                                    }))
                                                }
                                            }}
                                        >
                                            {showDateRange ? 'Disable Date Range' : 'Enable Date Range'}
                                        </button>
                                    </div>
                                    {showDateRange && (
                                        <div className={styles.dateRange}>
                                            <input
                                                type="date"
                                                value={customReportConfig.dateFrom}
                                                onChange={(e) =>
                                                    setCustomReportConfig((prev) => ({
                                                        ...prev,
                                                        dateFrom: e.target.value,
                                                    }))
                                                }
                                                placeholder="From Date"
                                                className={styles.dateInput}
                                            />
                                            <span className={styles.dateSeparator}>to</span>
                                            <input
                                                type="date"
                                                value={customReportConfig.dateTo}
                                                onChange={(e) =>
                                                    setCustomReportConfig((prev) => ({
                                                        ...prev,
                                                        dateTo: e.target.value,
                                                    }))
                                                }
                                                placeholder="To Date"
                                                className={styles.dateInput}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Filters Section - Only for Claims */}
                            {customReportConfig.tableGroup === 'claims' && (
                                <div className={styles.filtersSection}>
                                    <div className={styles.filtersHeader}>
                                        <label className={styles.configLabel}>Additional Filters</label>
                                        <button
                                            type="button"
                                            className={`${styles.filtersToggleButton} ${
                                                showFilters ? styles.active : ''
                                            }`}
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                                        </button>
                                    </div>
                                    {showFilters && (
                                        <div className={styles.filtersGrid}>
                                            <div className={styles.filterGroup}>
                                                <label className={styles.filterLabel}>Introducer</label>
                                                <div className={styles.introducerButtons}>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.introducer === 'all' ? styles.active : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                introducer: 'all',
                                                            }))
                                                        }
                                                    >
                                                        All
                                                    </button>
                                                    {introducerOptions.map((option) => (
                                                        <button
                                                            key={option.value}
                                                            type="button"
                                                            className={`${styles.filterButton} ${
                                                                customReportConfig.introducer !== 'all' &&
                                                                customReportConfig.introducer.includes(option.value)
                                                                    ? styles.active
                                                                    : ''
                                                            }`}
                                                            onClick={() =>
                                                                setCustomReportConfig((prev) => ({
                                                                    ...prev,
                                                                    introducer:
                                                                        prev.introducer === 'all'
                                                                            ? option.value
                                                                            : prev.introducer.includes(option.value)
                                                                            ? prev.introducer
                                                                                  .split(',')
                                                                                  .filter(
                                                                                      (i) => i.trim() !== option.value
                                                                                  )
                                                                                  .join(',') || 'all'
                                                                            : prev.introducer === 'all'
                                                                            ? option.value
                                                                            : prev.introducer + ',' + option.value,
                                                                }))
                                                            }
                                                        >
                                                            {option.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <br />

                                            <div className={styles.filterGroup}>
                                                <label className={styles.filterLabel}>Cancelled Status</label>
                                                <div className={styles.filterButtons}>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.cancelled === 'all' ? styles.active : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                cancelled: prev.cancelled === 'all' ? 'all' : 'all',
                                                            }))
                                                        }
                                                    >
                                                        All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.cancelled === true ? styles.active : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                cancelled: prev.cancelled === true ? 'all' : true,
                                                            }))
                                                        }
                                                    >
                                                        Cancelled
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.cancelled === false ? styles.active : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                cancelled: prev.cancelled === false ? 'all' : false,
                                                            }))
                                                        }
                                                    >
                                                        Active
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={styles.filterGroup}>
                                                <label className={styles.filterLabel}>Policy Number</label>
                                                <div className={styles.filterButtons}>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.hasPolicyNumber === 'all'
                                                                ? styles.active
                                                                : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                hasPolicyNumber:
                                                                    prev.hasPolicyNumber === 'all' ? 'all' : 'all',
                                                            }))
                                                        }
                                                    >
                                                        All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.hasPolicyNumber === true
                                                                ? styles.active
                                                                : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                hasPolicyNumber:
                                                                    prev.hasPolicyNumber === true ? 'all' : true,
                                                            }))
                                                        }
                                                    >
                                                        Has Policy Number
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.filterButton} ${
                                                            customReportConfig.hasPolicyNumber === false
                                                                ? styles.active
                                                                : ''
                                                        }`}
                                                        onClick={() =>
                                                            setCustomReportConfig((prev) => ({
                                                                ...prev,
                                                                hasPolicyNumber:
                                                                    prev.hasPolicyNumber === false ? 'all' : false,
                                                            }))
                                                        }
                                                    >
                                                        No Policy Number
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={styles.filterRow}>
                                                <div className={styles.filterGroup}>
                                                    <label className={styles.filterLabel}>Starting Balance</label>
                                                    <div className={styles.filterButtons}>
                                                        {startingBalanceOptions.map((option) => (
                                                            <button
                                                                key={
                                                                    option.value === 'all'
                                                                        ? 'all'
                                                                        : option.value
                                                                        ? 'true'
                                                                        : 'false'
                                                                }
                                                                type="button"
                                                                className={`${styles.filterButton} ${
                                                                    customReportConfig.hasStartingBalance ===
                                                                    option.value
                                                                        ? styles.active
                                                                        : ''
                                                                }`}
                                                                onClick={() =>
                                                                    setCustomReportConfig((prev) => ({
                                                                        ...prev,
                                                                        hasStartingBalance: option.value,
                                                                    }))
                                                                }
                                                            >
                                                                {option.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className={styles.filterGroup}>
                                                    <label className={styles.filterLabel}>Quantum Value</label>
                                                    <div className={styles.filterButtons}>
                                                        {quantumValueOptions.map((option) => (
                                                            <button
                                                                key={
                                                                    option.value === 'all'
                                                                        ? 'all'
                                                                        : option.value
                                                                        ? 'true'
                                                                        : 'false'
                                                                }
                                                                type="button"
                                                                className={`${styles.filterButton} ${
                                                                    customReportConfig.hasQuantumValue === option.value
                                                                        ? styles.active
                                                                        : ''
                                                                }`}
                                                                onClick={() =>
                                                                    setCustomReportConfig((prev) => ({
                                                                        ...prev,
                                                                        hasQuantumValue: option.value,
                                                                    }))
                                                                }
                                                            >
                                                                {option.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`${styles.filterGroup} ${styles.lenderFilterGroup}`}>
                                                <label className={styles.filterLabel}>
                                                    Filter by Approved Lenders
                                                    {customReportConfig.lenders !== 'all' && (
                                                        <span className={styles.lenderLimit}>
                                                            (
                                                            {
                                                                customReportConfig.lenders
                                                                    .split(',')
                                                                    .filter((l) => l.trim()).length
                                                            }
                                                            /10)
                                                        </span>
                                                    )}
                                                </label>
                                                <div className={styles.lenderSelectContainer}>
                                                    <div className={styles.lenderSelectRow}>
                                                        <div className={styles.lenderCheckboxSection}>
                                                            <div className={styles.lenderCheckboxList}>
                                                                {lendersLoading && lenderOptions.length === 0 && (
                                                                    <div className={styles.allLendersSelected}>
                                                                        Loading approved lenders...
                                                                    </div>
                                                                )}
                                                                {lenderOptions.map((lender) => (
                                                                    <label
                                                                        key={lender.value}
                                                                        className={styles.lenderCheckbox}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={
                                                                                customReportConfig.lenders !== 'all' &&
                                                                                customReportConfig.lenders
                                                                                    .split(',')
                                                                                    .filter((l) => l.trim())
                                                                                    .includes(lender.value)
                                                                            }
                                                                            disabled={
                                                                                customReportConfig.lenders !== 'all' &&
                                                                                customReportConfig.lenders
                                                                                    .split(',')
                                                                                    .filter((l) => l.trim()).length >=
                                                                                    10 &&
                                                                                !customReportConfig.lenders
                                                                                    .split(',')
                                                                                    .filter((l) => l.trim())
                                                                                    .includes(lender.value)
                                                                            }
                                                                            onChange={(e) => {
                                                                                const currentSelected =
                                                                                    customReportConfig.lenders === 'all'
                                                                                        ? []
                                                                                        : customReportConfig.lenders
                                                                                              .split(',')
                                                                                              .filter((l) => l.trim())

                                                                                let newSelected: string[]
                                                                                if (e.target.checked) {
                                                                                    // Check if we're already at the limit of 10 lenders
                                                                                    if (currentSelected.length >= 10) {
                                                                                        alert(
                                                                                            'Maximum of 10 lenders can be selected. Please deselect some lenders first.'
                                                                                        )
                                                                                        return
                                                                                    }
                                                                                    newSelected = [
                                                                                        ...currentSelected,
                                                                                        lender.value,
                                                                                    ]
                                                                                } else {
                                                                                    newSelected =
                                                                                        currentSelected.filter(
                                                                                            (l) => l !== lender.value
                                                                                        )
                                                                                }

                                                                                setCustomReportConfig((prev) => ({
                                                                                    ...prev,
                                                                                    lenders:
                                                                                        newSelected.length === 0
                                                                                            ? 'all'
                                                                                            : newSelected.join(','),
                                                                                }))
                                                                            }}
                                                                        />
                                                                        <span>{lender.label}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            <div className={styles.lenderActions}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setCustomReportConfig((prev) => ({
                                                                            ...prev,
                                                                            lenders: 'all',
                                                                        }))
                                                                    }
                                                                    className={styles.clearAllButton}
                                                                >
                                                                    Clear All
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className={styles.selectedLendersSection}>
                                                            <div className={styles.selectedLendersTitle}>
                                                                Selected Lenders:
                                                            </div>
                                                            <div className={styles.selectedLendersList}>
                                                                {customReportConfig.lenders === 'all' ? (
                                                                    <div className={styles.allLendersSelected}>
                                                                        All lenders selected
                                                                    </div>
                                                                ) : (
                                                                    customReportConfig.lenders
                                                                        .split(',')
                                                                        .filter((l) => l.trim())
                                                                        .map((lenderName) => {
                                                                            const lender = lenderOptions.find(
                                                                                (l) => l.value === lenderName
                                                                            )
                                                                            return (
                                                                                <div
                                                                                    key={lenderName}
                                                                                    className={
                                                                                        styles.selectedLenderItem
                                                                                    }
                                                                                >
                                                                                    {lender ? lender.label : lenderName}
                                                                                </div>
                                                                            )
                                                                        })
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={styles.generateSection}>
                                <button
                                    onClick={generateCustomReport}
                                    disabled={customReportConfig.selectedColumns.length === 0}
                                    className={styles.generateButton}
                                >
                                    <svg
                                        className={styles.generateIcon}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7,10 12,15 17,10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Generate Custom Report
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pre-configured Reports Section */}
                <div className={styles.preConfiguredSection}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Pre-configured Reports</h2>
                        <p className={styles.sectionSubtitle}>
                            Access our collection of ready-to-use reports and analytics
                        </p>
                    </div>

                    {/* Category Filter */}
                    <div className={styles.categoryFilter}>
                        {categories.map((category) => (
                            <button
                                key={category}
                                className={`${styles.categoryButton} ${
                                    selectedCategory === category ? styles.active : ''
                                }`}
                                onClick={() => setSelectedCategory(category)}
                            >
                                {category === 'all' ? 'All Reports' : category}
                            </button>
                        ))}
                    </div>

                    {/* Reports Grid */}
                    <div className={styles.reportsGrid}>
                        {selectedCategory === 'all' ? (
                            // Show grouped by category when "all" is selected
                            Object.entries(groupedLinks).map(([category, links]) => (
                                <div key={category} className={styles.categorySection}>
                                    <h3 className={styles.categoryTitle}>{category}</h3>
                                    <div className={styles.categoryGrid}>
                                        {links.map((link) => (
                                            <div key={link.id} className={styles.reportCard}>
                                                <div className={styles.cardHeader}>
                                                    <h4 className={styles.reportTitle}>{link.title}</h4>
                                                    <div className={styles.cardAccent} />
                                                </div>
                                                {renderDescription(link)}
                                                <div className={styles.cardActions}>
                                                    {link.id === 'daily-policy-number-report' ? (
                                                        <div className={styles.stackedActions}>
                                                            <input
                                                                type="date"
                                                                value={dailyPolicyDate}
                                                                onChange={(e) => setDailyPolicyDate(e.target.value)}
                                                                className={styles.dateInput}
                                                            />
                                                            <button
                                                                onClick={handleDailyPolicyReportDownload}
                                                                className={styles.downloadButton}
                                                                disabled={isLoading || !dailyPolicyDate}
                                                            >
                                                                <svg
                                                                    className={styles.downloadIcon}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                >
                                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                                    <polyline points="7,10 12,15 17,10" />
                                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                                </svg>
                                                                Download Report
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() =>
                                                                handleReportDownload(link.url, link.title, link.fileType)
                                                            }
                                                            className={styles.downloadButton}
                                                            disabled={isLoading}
                                                        >
                                                            <svg
                                                                className={styles.downloadIcon}
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                            >
                                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                                <polyline points="7,10 12,15 17,10" />
                                                                <line x1="12" y1="15" x2="12" y2="3" />
                                                            </svg>
                                                            Download Report
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            // Show filtered results when specific category is selected
                            <div className={styles.categoryGrid}>
                                {filteredLinks.map((link) => (
                                    <div key={link.id} className={styles.reportCard}>
                                        <div className={styles.cardHeader}>
                                            <h4 className={styles.reportTitle}>{link.title}</h4>
                                            <div className={styles.cardAccent} />
                                        </div>
                                        {renderDescription(link)}
                                        <div className={styles.cardActions}>
                                            {link.id === 'daily-policy-number-report' ? (
                                                <div className={styles.stackedActions}>
                                                    <input
                                                        type="date"
                                                        value={dailyPolicyDate}
                                                        onChange={(e) => setDailyPolicyDate(e.target.value)}
                                                        className={styles.dateInput}
                                                    />
                                                    <button
                                                        onClick={handleDailyPolicyReportDownload}
                                                        className={styles.downloadButton}
                                                        disabled={isLoading || !dailyPolicyDate}
                                                    >
                                                        <svg
                                                            className={styles.downloadIcon}
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                        >
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                            <polyline points="7,10 12,15 17,10" />
                                                            <line x1="12" y1="15" x2="12" y2="3" />
                                                        </svg>
                                                        Download Report
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleReportDownload(link.url, link.title, link.fileType)}
                                                    className={styles.downloadButton}
                                                    disabled={isLoading}
                                                >
                                                    <svg
                                                        className={styles.downloadIcon}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                    >
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                        <polyline points="7,10 12,15 17,10" />
                                                        <line x1="12" y1="15" x2="12" y2="3" />
                                                    </svg>
                                                    Download Report
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {detailsLink && (
                <Modal modalState previewModalAction={() => setDetailsLink(null)}>
                    <div className={styles.reportDetailsModal}>
                        <h3>{detailsLink.title}</h3>
                        <div className={styles.reportDetailsBody}>{detailsLink.fullDescription}</div>
                    </div>
                </Modal>
            )}
        </div>
    )
}
